// src/components/BlogPost.tsx
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const PostContainer = styled.article`
    max-width: 800px;
    margin: 0 auto;
    background: #ffffff;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);
`;

const PostHeader = styled.div`
    position: relative;
    padding: 3rem 3rem 2rem;
    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);

    @media (max-width: 768px) {
        padding: 2rem 1.5rem 1.5rem;
    }
`;

const CategoryTag = styled.span`
    display: inline-block;
    padding: 0.5rem 1.25rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 700;
    color: #ffffff;
    background: linear-gradient(135deg, #990147 0%, #cc3366 100%);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 1.5rem;
`;

const PostTitle = styled(motion.h1)`
    font-size: 2.5rem;
    font-weight: 800;
    color: #000000;
    line-height: 1.2;
    margin-bottom: 1rem;

    @media (max-width: 768px) {
        font-size: 2rem;
    }

    @media (max-width: 480px) {
        font-size: 1.75rem;
    }
`;

const PostMeta = styled.div`
    display: flex;
    gap: 1.5rem;
    align-items: center;
    margin-bottom: 2rem;

    @media (max-width: 480px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.5rem;
    }
`;

const MetaItem = styled.span`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    color: #4a5568;
    font-weight: 500;
`;

const FeaturedImage = styled(motion.div)`
    width: 100%;
    height: 400px;
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 2rem;

    @media (max-width: 768px) {
        height: 300px;
    }

    @media (max-width: 480px) {
        height: 250px;
    }
`;

const PostImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const PostContent = styled.div`
    padding: 0 3rem 3rem;

    @media (max-width: 768px) {
        padding: 0 1.5rem 2rem;
    }
`;

const ContentSection = styled(motion.section)`
    margin-bottom: 2.5rem;

    &:last-child {
        margin-bottom: 0;
    }
`;

const ContentTitle = styled.h2`
    font-size: 1.5rem;
    font-weight: 700;
    color: #000000;
    margin-bottom: 1rem;
    line-height: 1.3;

    @media (max-width: 768px) {
        font-size: 1.3rem;
    }
`;

const ContentParagraph = styled.p`
    font-size: 1.1rem;
    line-height: 1.7;
    color: #4a5568;
    margin-bottom: 1.5rem;

    @media (max-width: 768px) {
        font-size: 1rem;
        line-height: 1.6;
    }
`;

const ContentList = styled.ul`
    margin: 1.5rem 0;
    padding-left: 1.5rem;
`;

const ListItem = styled.li`
    font-size: 1.1rem;
    line-height: 1.7;
    color: #4a5568;
    margin-bottom: 0.75rem;

    @media (max-width: 768px) {
        font-size: 1rem;
    }
`;

const QuoteBlock = styled(motion.blockquote)`
    background: linear-gradient(135deg, rgba(153, 1, 71, 0.05) 0%, rgba(204, 51, 102, 0.05) 100%);
    border-left: 4px solid #990147;
    padding: 2rem;
    margin: 2rem 0;
    border-radius: 0 12px 12px 0;
    
    p {
        font-size: 1.2rem;
        font-style: italic;
        color: #2d3748;
        line-height: 1.6;
        margin: 0;
    }
    
    @media (max-width: 768px) {
        padding: 1.5rem;
        
        p {
            font-size: 1.1rem;
        }
    }
`;

const PostFooter = styled.div`
    padding: 2rem 3rem;
    background: #f8fafc;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    
    @media (max-width: 768px) {
        padding: 1.5rem;
    }
`;

const ShareSection = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    
    span {
        font-weight: 600;
        color: #4a5568;
    }
`;

const ShareButton = styled(motion.button)`
    padding: 0.5rem 1rem;
    background: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    font-size: 0.9rem;
    color: #4a5568;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
        background: #f7fafc;
        border-color: #990147;
        color: #990147;
    }
`;

// Updated interfaces to match backend DTO
interface PostSection {
    id: number;
    sectionType: 'HEADING' | 'PARAGRAPH' | 'LIST' | 'QUOTE' | 'IMAGE';
    content: string;
    sectionOrder: number;
}

interface BlogPostProps {
    post: {
        id: number;
        title: string;
        excerpt: string;
        featuredImage?: string;
        readTime: string;
        createdAt: string;
        updatedAt: string;
        category: {
            id: number;
            name: string;
        };
        sections: PostSection[];
    };
}

const BlogPost: React.FC<BlogPostProps> = ({ post }) => {
    const handleShare = () => {
        if (navigator.share) {
            navigator.share({
                title: post.title,
                text: post.excerpt,
                url: window.location.href,
            });
        } else {
            navigator.clipboard.writeText(window.location.href);
            alert('Link bol skopírovaný do schránky!');
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('sk-SK', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Helper function to render section content based on type
    const renderSectionContent = (section: PostSection, index: number) => {
        switch (section.sectionType) {
            case 'HEADING':
                return <ContentTitle>{section.content}</ContentTitle>;
            case 'PARAGRAPH':
                return <ContentParagraph>{section.content}</ContentParagraph>;
            case 'LIST':
                const listItems = section.content.split('\n').filter(item => item.trim());
                return (
                    <ContentList>
                        {listItems.map((item, lIndex) => (
                            <ListItem key={lIndex}>{item}</ListItem>
                        ))}
                    </ContentList>
                );
            case 'QUOTE':
                return (
                    <QuoteBlock
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                    >
                        <p>"{section.content}"</p>
                    </QuoteBlock>
                );
            case 'IMAGE':
                return (
                    <FeaturedImage
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        <PostImage src={section.content} alt="Section image" />
                    </FeaturedImage>
                );
            default:
                return <ContentParagraph>{section.content}</ContentParagraph>;
        }
    };

    return (
        <PostContainer>
            <PostHeader>
                <CategoryTag>{post.category.name}</CategoryTag>
                <PostTitle
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    {post.title}
                </PostTitle>
                <PostMeta>
                    <MetaItem>📅 {formatDate(post.createdAt)}</MetaItem>
                    <MetaItem>⏱️ {post.readTime}</MetaItem>
                </PostMeta>
                {post.featuredImage && (
                    <FeaturedImage
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        <PostImage src={post.featuredImage} alt={post.title} />
                    </FeaturedImage>
                )}
            </PostHeader>

            <PostContent>
                {post.sections
                    .sort((a, b) => a.sectionOrder - b.sectionOrder)
                    .map((section, index) => (
                        <ContentSection
                            key={section.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                        >
                            {renderSectionContent(section, index)}
                        </ContentSection>
                    ))}
            </PostContent>

            <PostFooter>
                <ShareSection>
                    <span>Podeliť sa:</span>
                    <ShareButton
                        onClick={handleShare}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        📤 Zdieľať
                    </ShareButton>
                </ShareSection>
            </PostFooter>
        </PostContainer>
    );
};

export default BlogPost;